import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SuggestedIcon } from "components/ArtificialIntelligence/SuggestedIcon";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import { KeyboardEvent, ReactNode, memo } from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
} from "react-hook-form";
import { FieldWarningIndicator } from "routes/Customer/Waste/Manifests/FieldWarningIndicator";

import { RevertHelperLine } from "../ReviewDocumentMetadataDialog/components/RevertHelperLine";
import {
  useAcknowledgement,
  useArrival,
  useAttentionTarget,
  useAttentionTravel,
} from "./AttentionTargets";
import {
  ExtractionReviewFormData,
  displayDraft,
  isFlagged,
  isRecordConflict,
  isReviewed,
} from "./formData";
import { RecordDiffTooltip } from "./RecordDiffTooltip";
import { ExtractionDraftValue, ExtractionFieldSpec, TRI_BOOLEAN_OPTIONS } from "./types";

function lowConfidenceTooltip(score: number | null): string {
  if (score == null) return "The extractor was not confident about this value — please verify it.";
  return `The extractor was ${Math.round(score * 100)}% confident about this value — please verify it.`;
}

/** The same sentence, folded into the disagreement tooltip when both apply. */
function lowConfidenceNote(score: number | null): string {
  if (score == null) return "The extractor was not confident about its reading.";
  return `The extractor was ${Math.round(score * 100)}% confident about its reading.`;
}

export interface ExtractionFieldControlProps {
  spec: ExtractionFieldSpec;
  control: Control<ExtractionReviewFormData>;
  path: FieldPath<ExtractionReviewFormData>;
  /** What the extractor proposed for this path, in draft form. */
  extracted: ExtractionDraftValue;
  /** False on a reviewer-added row, which the extractor never proposed. */
  hasCandidate: boolean;
  /** The extractor's confidence for this path, or null when it gave none. */
  confidence: number | null;
  /**
   * How an answer about this control is remembered — a field key, or a row cell
   * keyed to its extracted row. Not the form path, which renumbers when a row is
   * removed and would move a reviewer's answer to a different cell. Null on a
   * control nothing can be answered about.
   */
  acknowledgeKey: string | null;
  /**
   * What the record this review will be written over holds for this path, or
   * `undefined` when the caller supplied no record at all — which is not the same
   * as a record that holds nothing here.
   */
  recordValue?: ExtractionDraftValue;
  lowConfidenceThreshold: number;
  /**
   * Visible label, inside the input as everywhere else in the app. A repeated
   * column keeps its plain label — the row it belongs to is named by the
   * surrounding group, so nothing has to repeat the row per field.
   */
  label: string;
  disabled: boolean;
}

type FieldRenderProps = {
  field: ControllerRenderProps<ExtractionReviewFormData>;
  fieldState: ControllerFieldState;
};

/**
 * One extracted value, as an always-editable labelled control.
 *
 * Carries the two affordances the document-metadata review established: the
 * shared `SuggestedIcon` sparkle for a value the AI filled that nobody has
 * touched, and — layered on for this surface — a `FieldWarningIndicator` on the
 * end adornment (the slot `reviewDiffAdornment` uses for sources-disagree) when
 * the extractor's confidence was low, or when the value disagrees with what the
 * record already holds.
 *
 * Memoized on its own props, which are all primitives or values stable for the
 * life of the schema. Without it a keystroke re-rendered every control in the
 * dialog: the form-wide `useWatch` behind the attention walk re-renders the whole
 * grid on every change, and a real profile sheet puts 95 of these on screen, each
 * carrying a `Controller` and a MUI input. Its own value still arrives through
 * that `Controller`, so the field the reviewer is typing in updates as it did.
 *
 * Both read `isReviewed`, a comparison against this value's own extracted
 * candidate, rather than `SuggestedField`/`useSuggestedValue`'s dirty check. In a
 * repeated group RHF's dirty state is positional, so removing a row would retire
 * every surviving row's flag; see `isReviewed` for the full reasoning.
 */
export const ExtractionFieldControl = memo(function ExtractionFieldControl({
  spec,
  control,
  path,
  extracted,
  hasCandidate,
  confidence,
  acknowledgeKey,
  recordValue,
  lowConfidenceThreshold,
  label,
  disabled,
}: ExtractionFieldControlProps) {
  const targetRef = useAttentionTarget(path);
  const { isAcknowledged, acknowledge } = useAcknowledgement(acknowledgeKey);
  const { hasArrived, dismiss } = useArrival(path);
  const { markPosition } = useAttentionTravel();

  return (
    <Controller
      name={path}
      control={control}
      render={({ field, fieldState }) => {
        const value: ExtractionDraftValue =
          typeof field.value === "string" || typeof field.value === "boolean" ? field.value : null;
        const reviewed = hasCandidate ? isReviewed(spec, value, extracted) : true;
        const flagged = !isAcknowledged && isFlagged(confidence, reviewed, lowConfidenceThreshold);
        // "The extractor produced something here" — the rule both the sparkle and
        // the revert line hang off, so they cannot disagree about which fields
        // have a reading behind them. Deliberately not `isBlankDraft`, which
        // counts boolean false as blank and would hide both from a checkbox the
        // extractor genuinely read as false.
        const hasReading =
          extracted !== null && !(typeof extracted === "string" && extracted.trim() === "");
        const suggested = hasCandidate && !reviewed && hasReading;

        // An answered disagreement stays answered: the reviewer looked at both
        // values and kept this one, so the indicator, the banner count and travel
        // all stop asking. Editing is the other way out, and clears it on its own.
        const conflicted =
          !isAcknowledged &&
          isRecordConflict({ spec, current: value, extracted, recordValue, hasCandidate });
        // Null rather than an empty element when there is nothing to say, so the
        // input's end adornment stays absent instead of rendering a blank slot.
        const warning =
          conflicted || flagged ? (
            <FieldAttention
              label={label}
              flagged={flagged}
              confidence={confidence}
              currentValue={value}
              recordValue={conflicted ? recordValue : undefined}
              onTake={(taken) => field.onChange(taken)}
              onKeep={acknowledge}
              forceOpen={hasArrived}
            />
          ) : null;

        // Nothing to restore when the extractor read nothing: the field is
        // showing something the record already held, and "Suggested: (empty)"
        // beside a live Revert offers to erase it in one click, labelled as
        // putting back a reading that was never made.
        const revertLine =
          hasCandidate && reviewed && hasReading ? (
            <RevertHelperLine
              // "Suggested" is what the rest of the product calls a value the AI
              // proposed — the sparkle, `SuggestedField`, "Suggested title" in the
              // document review. "Extracted" named the pipeline instead.
              label="Suggested"
              displayValue={displayDraft(extracted)}
              tooltip={`Restore what we read for ${label}`}
              onClick={() => field.onChange(extracted)}
            />
          ) : null;

        // Blur closes the arrival, but the indicator keeps the tooltip up while the
        // pointer is on it — which is how clicking a value inside it survives the
        // field losing focus. Escape closes it outright.
        const onBlur = () => {
          dismiss();
          field.onBlur();
        };
        // Wherever the reviewer puts themselves is where "jump to next" counts
        // from. Without this it kept counting from the last jump, so clicking into
        // a field halfway down the form and pressing it sent them back up.
        const onFocus = () => markPosition(path);
        // Stopped only when it actually closed something. The review sits in a
        // dialog that closes on Escape, so an unstopped keypress meant for the
        // tooltip discards the whole review — and with it every acknowledgement,
        // which lives in session state. Plain Escape on a field with no tooltip
        // open still closes the dialog, as it did before travel existed.
        const onEscape = (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== "Escape" || !hasArrived) return;
          dismiss();
          event.stopPropagation();
        };

        const body = {
          spec,
          label,
          disabled,
          value,
          suggested,
          warning,
          revertLine,
          targetRef,
          onBlur,
          onFocus,
          onEscape,
        };
        if (spec.kind === "boolean") {
          return <BooleanFieldBody {...body} field={field} fieldState={fieldState} />;
        }
        return <TextFieldBody {...body} field={field} fieldState={fieldState} />;
      }}
    />
  );
});

/**
 * The one warning icon a field can carry: either the value disagrees with the
 * record it would replace, or the extractor was unsure of it.
 *
 * One icon, never two. A value can be both, and stacking two warnings on one
 * field says less than one warning that states both — so the disagreement takes
 * the slot, since it is the one with an action behind it, and folds the
 * confidence in as a note.
 */
function FieldAttention({
  label,
  flagged,
  confidence,
  currentValue,
  recordValue,
  onTake,
  onKeep,
  forceOpen,
}: {
  label: string;
  flagged: boolean;
  confidence: number | null;
  currentValue: ExtractionDraftValue;
  /** Set only when this value would replace a different one on the record. */
  recordValue: ExtractionDraftValue | undefined;
  onTake: (value: ExtractionDraftValue) => void;
  onKeep: () => void;
  /** True when travel has just landed here, so the reason shows without a hover. */
  forceOpen: boolean;
}): ReactNode {
  if (recordValue !== undefined) {
    return (
      <FieldWarningIndicator
        tooltip={
          <RecordDiffTooltip
            recordDisplay={displayDraft(recordValue)}
            readingDisplay={displayDraft(currentValue)}
            recordValue={recordValue}
            onTake={onTake}
            onKeep={onKeep}
            lowConfidenceNote={flagged ? lowConfidenceNote(confidence) : undefined}
          />
        }
        ariaLabel={`Differs from the record: ${label}`}
        inline
        interactive
        forceOpen={forceOpen}
      />
    );
  }
  if (flagged) {
    return (
      <FieldWarningIndicator
        tooltip={<LowConfidenceTooltip confidence={confidence} onAccept={onKeep} />}
        ariaLabel={`Low confidence: ${label}`}
        inline
        interactive
        forceOpen={forceOpen}
      />
    );
  }
  return null;
}

/**
 * Why the value is flagged, and a way to say it reads right anyway.
 *
 * An unsure reading is often a correct one — the extractor hedges on a smudged
 * scan of the right number — so a reviewer who has checked it needs to be able to
 * say so. Without that the banner never reaches zero and travel keeps returning
 * to a value already verified. Accepting is not an edit: nothing about the value
 * changes, so nothing is recorded as a correction.
 */
function LowConfidenceTooltip({
  confidence,
  onAccept,
}: {
  confidence: number | null;
  onAccept: () => void;
}) {
  return (
    <Box>
      <Typography variant="caption" sx={{ display: "block" }}>
        {lowConfidenceTooltip(confidence)}
      </Typography>
      <Button
        size="small"
        color="inherit"
        onClick={onAccept}
        sx={{ mt: 0.5, px: 0, minWidth: 0, textDecoration: "underline" }}
      >
        Dismiss warning
      </Button>
    </Box>
  );
}

type FieldBodyProps = FieldRenderProps & {
  spec: ExtractionFieldSpec;
  label: string;
  disabled: boolean;
  value: ExtractionDraftValue;
  suggested: boolean;
  warning: ReactNode;
  revertLine: ReactNode;
  /** Registers this control as where travel to its path lands. */
  targetRef: (element: HTMLElement | null) => void;
  onBlur: () => void;
  onFocus: () => void;
  onEscape: (event: KeyboardEvent<HTMLDivElement>) => void;
};

function BooleanFieldBody({
  label,
  disabled,
  value,
  suggested,
  warning,
  revertLine,
  field,
  targetRef,
  onBlur,
  onFocus,
  onEscape,
}: FieldBodyProps) {
  return (
    // Same handlers the text body wires. Without them a checkbox never reports
    // where the reviewer is, so travel counted from the last jump instead, and an
    // arrival on one was never dismissed.
    <Box ref={targetRef} onKeyDown={onEscape}>
      <Stack direction="row" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={value === true}
              onChange={(event) => field.onChange(event.target.checked)}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={disabled}
              size="small"
            />
          }
          label={label}
        />
        {suggested && <SuggestedIcon isSuggested size="small" />}
        {warning}
      </Stack>
      {revertLine}
    </Box>
  );
}

function TextFieldBody({
  spec,
  label,
  disabled,
  value,
  suggested,
  warning,
  revertLine,
  field,
  fieldState,
  targetRef,
  onBlur,
  onFocus,
  onEscape,
}: FieldBodyProps) {
  const textValue = typeof value === "string" ? value : "";
  const isTriBoolean = spec.kind === "triBoolean";
  const isSelect = spec.kind === "select" || isTriBoolean;
  // A tri-state's options are fixed; a select's come from the caller. The
  // empty option below is what carries "not answered".
  const options = isTriBoolean ? TRI_BOOLEAN_OPTIONS : (spec.options ?? []);
  // An extractor can emit a value outside the caller's option list. Keeping
  // it as an option means the reviewer sees what will be committed instead
  // of a blank field hiding a value they never agreed to.
  const unrecognizedOption =
    isSelect && textValue !== "" && !options.some((option) => option.value === textValue)
      ? textValue
      : null;
  // safeParseFloat nulls anything it can't read; say so rather than
  // discarding what the reviewer typed without comment.
  const unparseableNumber =
    spec.kind === "number" && textValue.trim() !== "" && safeParseFloat(textValue) == null;

  const helperText = (() => {
    if (fieldState.error?.message != null) return fieldState.error.message;
    if (unparseableNumber) return "Enter a number";
    if (unrecognizedOption != null) return "Not one of the expected values — please confirm";
    return spec.helperText;
  })();

  // A select's dropdown arrow is absolutely positioned at the input's trailing
  // edge, so anything in the end adornment lands underneath it. Elsewhere the app
  // sidesteps this by owning the indicator — the pickers are Autocompletes, which
  // render theirs as a flow child and then the extra node after it. A native
  // select cannot do that without giving up arrow-click-to-open, so instead the
  // arrow moves inboard and the adornment keeps the edge, which lands on the same
  // order those pickers produce: indicator, then the warning.
  const adornedSelect = isSelect && (warning != null || spec.unit != null);

  return (
    <Box ref={targetRef}>
      <TextField
        select={isSelect}
        sx={
          adornedSelect
            ? {
                "& .MuiSelect-icon": { right: 30 },
                // Room for both, so a long option does not run under either.
                "& .MuiSelect-select": { pr: 7 },
              }
            : undefined
        }
        multiline={spec.kind === "multiline"}
        minRows={spec.kind === "multiline" ? 2 : undefined}
        size="small"
        fullWidth
        label={label}
        disabled={disabled}
        value={textValue}
        name={field.name}
        inputRef={field.ref}
        // Normalizing "" back to null keeps a cleared field indistinguishable
        // from one the extractor never filled, which is what lets a
        // type-then-delete stop counting as a review. Matches FormTextField.
        onChange={(event) => field.onChange(event.target.value === "" ? null : event.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onEscape}
        error={!!fieldState.error || unparseableNumber}
        helperText={helperText}
        slotProps={{
          input: {
            startAdornment: suggested ? <SuggestedAdornment spec={spec} /> : undefined,
            endAdornment:
              spec.unit != null || warning != null ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {spec.unit != null && (
                    <Typography variant="body2" color="text.secondary">
                      {spec.unit}
                    </Typography>
                  )}
                  {warning}
                </Stack>
              ) : undefined,
          },
          htmlInput: {
            inputMode: spec.kind === "number" ? "decimal" : undefined,
          },
        }}
      >
        {isSelect && <MenuItem value="">—</MenuItem>}
        {unrecognizedOption != null && (
          <MenuItem value={unrecognizedOption}>{unrecognizedOption}</MenuItem>
        )}
        {isSelect &&
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
      </TextField>
      {revertLine}
    </Box>
  );
}

function SuggestedAdornment({ spec }: { spec: ExtractionFieldSpec }) {
  return (
    <Box
      sx={{
        display: "flex",
        // A multiline input grows downward and the row centres its children, so
        // the sparkle drifts away from the value it marks. Pinned to the top
        // instead, with the icon's own block margin dropped: the input's padding
        // already places the adornment on the first line, and a 20px icon fills
        // that line's 20.125px box, so any margin on top of it hangs the icon
        // below the line.
        ...(spec.kind === "multiline"
          ? { alignSelf: "flex-start", "& .MuiSvgIcon-root": { my: 0 } }
          : {}),
      }}
    >
      <SuggestedIcon isSuggested size="small" />
    </Box>
  );
}
